import { inject } from "@angular/core";
import { Routes, UrlMatcher, UrlSegment } from "@angular/router";
import { ENTITY_PREFIX, stripPrefix, TypedEntityId } from "./shared/models/entity.model";

import { authGuard } from "./auth/guards/auth.guard";
import { roleGuard } from "./auth/guards/role.guard";
import { tenantInteractivesResolver } from "./layouts/tenant-interactive-layout/resolvers/tenant-interactives.resolver";
import { interactivesResolver } from "./layouts/tenant-interactive-layout/resolvers/interactives.resolver";
import { NavbarService } from "./layouts/tenant-interactive-layout/navbar.service";
import { NavigationService } from "./shared/services/navigation.service";

import { AdminLayoutComponent } from "./admin/admin-layout/admin-layout.component";
import { AuthCallbackComponent } from "./auth/auth-callback/auth-callback.component";
import { BaseLayoutComponent } from "./layouts/base-layout/base-layout.component";
import { CreateInteractiveComponent } from "./interactives/pages/create-interactive/create-interactive.component";
import { CreateTenantComponent } from "./admin/tenants/pages/create-tenant/create-tenant.component";
import { CreateThemeComponent } from "./admin/themes/pages/create-theme/create-theme.component";
import { CreateUserComponent } from "./admin/users/pages/create-user/create-user.component";
import { DuplicateInteractiveComponent } from "./interactives/pages/duplicate-interactive/duplicate-interactive.component";
import { EditInteractiveComponent } from "./interactives/pages/edit-interactive/edit-interactive.component";
import { EditTenantComponent } from "./admin/tenants/pages/edit-tenant/edit-tenant.component";
import { EditThemeComponent } from "./admin/themes/pages/edit-theme/edit-theme.component";
import { EditUserComponent } from "./admin/users/pages/edit-user/edit-user.component";
import { ErrorPageComponent } from "./error-page/error-page.component";
import { ForgotPasswordComponent } from "./auth/forgot-password/forgot-password.component";
import { FreeformDashboardComponent } from "./interactives/submissions/pages/producer/freeform-dashboard/freeform-dashboard.component";
import { GalleryComponent } from "./gallery/gallery.component";
import { HostFreeformComponent } from "./interactives/submissions/pages/host/host-freeform/host-freeform.component";
import { HostPollComponent } from "./interactives/submissions/pages/host/host-poll/host-poll.component";
import { InteractivesComponent } from "./interactives/pages/interactives/interactives.component";
import { LoginComponent } from "./auth/login/login.component";
import { PollDashboardComponent } from "./interactives/submissions/pages/producer/poll-dashboard/poll-dashboard.component";
import { TenantInteractiveLayoutComponent } from "./layouts/tenant-interactive-layout/tenant-interactive-layout.component";
import { TenantsComponent } from "./admin/tenants/pages/tenants/tenants.component";
import { ThemesComponent } from "./admin/themes/pages/themes/themes.component";
import { UsersComponent } from "./admin/users/pages/users/users.component";

// Matches a single typed interactive id segment (<prefix>:<uuid> where prefix is "IF" or "IP")
export function interactiveMatcher(
    prefix: typeof ENTITY_PREFIX.INTERACTIVE_FREEFORM | typeof ENTITY_PREFIX.INTERACTIVE_POLL
): UrlMatcher {
    return (segments: UrlSegment[]) => {
        if (segments.length === 1 && segments[0].path.startsWith(`${prefix}:`)) {
            return {
                consumed: segments,
                posParams: {
                    // Strip the type prefix so child routes receive a plain UUID.
                    // The original segment stays in `consumed`, keeping the prefix in the URL.
                    interactiveId: new UrlSegment(stripPrefix(segments[0].path as TypedEntityId), {})
                }
            };
        }
        return null;
    };
}

// Routes with both `component` and `children` use the parent component as a layout.
// The layout renders the matched child route inside its own <router-outlet>, so each
// group below inherits the navbar of the layout it nests under.
export const routes: Routes = [
    { path: "", pathMatch: "full", redirectTo: () => inject(NavigationService).homeRoute() },
    { path: "login", component: LoginComponent, data: { excludeFromLastVisited: true } },
    { path: "auth/callback", component: AuthCallbackComponent, data: { excludeFromLastVisited: true } },
    { path: "forgot-password", component: ForgotPasswordComponent, data: { excludeFromLastVisited: true } },
    {
        path: "",
        component: BaseLayoutComponent,
        canActivate: [authGuard],
        children: [
            { path: "gallery", component: GalleryComponent, data: { excludeFromLastVisited: true } },
            {
                path: "interactives",
                component: InteractivesComponent,
                data: { navTitle: "All Interactives", navIcon: "icon-iec-cards-three-fill" },
                canActivate: [roleGuard("isAdmin", "isProducer", "isMarketer")]
            },
            {
                path: "interactives/new",
                component: CreateInteractiveComponent,
                data: { navTitle: "All Interactives", navIcon: "icon-iec-cards-three-fill" },
                canActivate: [roleGuard("isAdmin", "isProducer")]
            },
            {
                path: "interactives/:interactiveId/edit",
                component: EditInteractiveComponent,
                data: { navTitle: "All Interactives", navIcon: "icon-iec-cards-three-fill" },
                canActivate: [roleGuard("isAdmin", "isProducer")]
            },
            {
                path: "interactives/:interactiveId/duplicate",
                component: DuplicateInteractiveComponent,
                data: { navTitle: "All Interactives", navIcon: "icon-iec-cards-three-fill" },
                canActivate: [roleGuard("isAdmin", "isProducer")]
            },
            {
                path: "admin",
                data: { navTitle: "Admin Panel", navIcon: "icon-iec-cog" },
                canActivate: [roleGuard("isAdmin")],
                children: [
                    // Tabbed admin sub-pages
                    {
                        path: "",
                        component: AdminLayoutComponent,
                        children: [
                            { path: "", redirectTo: "themes", pathMatch: "full" },
                            { path: "themes", component: ThemesComponent },
                            { path: "tenants", component: TenantsComponent },
                            { path: "users", component: UsersComponent }
                        ]
                    },
                    // Sibling routes — same /admin section, no tabs
                    { path: "themes/new", component: CreateThemeComponent },
                    { path: "themes/:themeId/edit", component: EditThemeComponent },
                    { path: "tenants/new", component: CreateTenantComponent },
                    { path: "tenants/:tenantId/edit", component: EditTenantComponent },
                    { path: "users/new", component: CreateUserComponent },
                    { path: "users/:userId/edit", component: EditUserComponent }
                ]
            }
        ]
    },
    {
        path: "interactives",
        component: TenantInteractiveLayoutComponent,
        canActivate: [authGuard],
        providers: [NavbarService],
        resolve: { interactives: tenantInteractivesResolver },
        children: [
            {
                path: "producer",
                data: { navTitle: "Producer Dashboard", navIcon: "icon-table" },
                canActivate: [roleGuard("isAdmin", "isProducer", "isMarketer")],
                children: [
                    {
                        matcher: interactiveMatcher(ENTITY_PREFIX.INTERACTIVE_FREEFORM),
                        component: FreeformDashboardComponent,
                        data: { interactiveType: "freeform" },
                        resolve: { interactives: interactivesResolver }
                    },
                    {
                        matcher: interactiveMatcher(ENTITY_PREFIX.INTERACTIVE_POLL),
                        component: PollDashboardComponent,
                        data: { interactiveType: "poll" },
                        resolve: { interactives: interactivesResolver }
                    }
                ]
            },
            {
                path: "host",
                data: { navTitle: "Host View", navIcon: "icon-iec-speaker" },
                canActivate: [roleGuard("isAdmin", "isProducer", "isHost")],
                children: [
                    {
                        matcher: interactiveMatcher(ENTITY_PREFIX.INTERACTIVE_FREEFORM),
                        component: HostFreeformComponent,
                        data: { interactiveType: "freeform" },
                        resolve: { interactives: interactivesResolver }
                    },
                    {
                        matcher: interactiveMatcher(ENTITY_PREFIX.INTERACTIVE_POLL),
                        component: HostPollComponent,
                        data: { interactiveType: "poll" },
                        resolve: { interactives: interactivesResolver }
                    }
                ]
            }
        ]
    },
    // Must stay last: anything unmatched above lands here.
    {
        path: "",
        component: BaseLayoutComponent,
        canActivate: [authGuard],
        children: [
            {
                path: "**",
                component: ErrorPageComponent,
                data: { excludeFromLastVisited: true }
            }
        ]
    }
];
